import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

import {
  DropdownSize,
  DropdownShape,
  DropdownWrapper,
  DropdownTrigger,
  ChevronWrap,
  DropdownList,
  DropdownItem,
  DropdownLabel,
} from './styles';

let _uid = 0;
const genId = (): string => `dropdown-${++_uid}`;

type Rect = { top: number; left: number; width: number; height: number };

export type { DropdownSize, DropdownShape } from './styles';

export type DropdownOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

/**
 * Dropdown — select customizado com dropdown estilizado.
 *
 * Usa o padrão ARIA Listbox (role combobox + listbox + option) com navegação
 * por teclado (↑↓ Enter Escape). NÃO usa `<select>` nativo, então o dropdown
 * é 100% estilizável (sem OS-native popup).
 *
 * Para um select nativo simples use o componente `Select`.
 *
 * @example
 * <Dropdown
 *   label="Meses"
 *   placeholder="Selecione o mês"
 *   size="xl"
 *   shape="pill"
 *   options={MESES}
 *   value={mes}
 *   onChange={setMes}
 * />
 */
export type DropdownProps = {
  options: DropdownOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  label?: string;
  size?: DropdownSize;
  shape?: DropdownShape;
  fullWidth?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
};

const ChevronIcon = (): React.ReactElement => (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3 5.5L8 10.5L13 5.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const Dropdown = ({
  options,
  value,
  onChange,
  placeholder = 'Selecione',
  label,
  size = 'md',
  shape = 'default',
  fullWidth = false,
  disabled = false,
  id: idProp,
  className,
  style,
}: DropdownProps): React.ReactElement => {
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [triggerRect, setTriggerRect] = useState<Rect | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const uid = useRef(genId()).current;
  const triggerId = idProp || `dropdown-trigger-${uid}`;
  const listId = `dropdown-list-${uid}`;

  const selectedOption = options.find((o) => o.value === value);

  // Recalculate trigger position on open and on scroll/resize
  const updateRect = useCallback((): void => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setTriggerRect({ top: r.bottom + window.scrollY, left: r.left + window.scrollX, width: r.width, height: r.height });
    }
  }, []);

  // Close on outside click / scroll / resize
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent): void => {
      if (
        wrapperRef.current && !wrapperRef.current.contains(e.target as Node) &&
        listRef.current && !listRef.current.contains(e.target as Node)
      ) setOpen(false);
    };
    const onScroll = (): void => { updateRect(); };
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', updateRect);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [open, updateRect]);

  // Scroll focused item into view
  useEffect(() => {
    if (!open || focusedIndex < 0) return;
    const item = listRef.current?.children[focusedIndex] as HTMLElement | null;
    item?.scrollIntoView?.({ block: 'nearest' });
  }, [focusedIndex, open]);

  const handleTriggerClick = (): void => {
    if (disabled) return;
    if (!open) {
      updateRect();
      setFocusedIndex(selectedOption ? options.findIndex((o) => o.value === value) : -1);
    }
    setOpen((prev) => !prev);
  };

  const handleSelect = (opt: DropdownOption): void => {
    if (opt.disabled) return;
    onChange?.(opt.value);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!open) {
          setOpen(true);
          setFocusedIndex(selectedOption ? options.findIndex((o) => o.value === value) : 0);
        } else if (focusedIndex >= 0) {
          handleSelect(options[focusedIndex]);
        }
        break;
      case 'Escape':
        setOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!open) { setOpen(true); setFocusedIndex(0); break; }
        setFocusedIndex((prev) => {
          let next = prev + 1;
          while (next < options.length && options[next].disabled) next++;
          return next < options.length ? next : prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => {
          let next = prev - 1;
          while (next >= 0 && options[next].disabled) next--;
          return next >= 0 ? next : prev;
        });
        break;
      case 'Tab':
        setOpen(false);
        break;
    }
  };

  const triggerLabel = selectedOption?.label || placeholder;

  return (
    <DropdownWrapper ref={wrapperRef} $fullWidth={fullWidth} className={className} style={style}>
      {label && (
        <DropdownLabel htmlFor={triggerId}>{label}</DropdownLabel>
      )}

      <DropdownTrigger
        ref={triggerRef}
        type="button"
        id={triggerId}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-disabled={disabled}
        $size={size}
        $shape={shape}
        $open={open}
        $hasValue={!!selectedOption}
        $fullWidth={fullWidth}
        onClick={handleTriggerClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {triggerLabel}
        </span>
        <ChevronWrap $open={open}>
          <ChevronIcon />
        </ChevronWrap>
      </DropdownTrigger>

      {open && triggerRect && ReactDOM.createPortal(
        <DropdownList
          ref={listRef}
          id={listId}
          role="listbox"
          aria-labelledby={triggerId}
          style={{
            position: 'absolute',
            top: triggerRect.top + 4,
            left: triggerRect.left,
            width: triggerRect.width,
            zIndex: 9999,
          }}
        >
          {options.map((opt, idx) => (
            <DropdownItem
              key={opt.value}
              role="option"
              id={`${listId}-option-${idx}`}
              aria-selected={opt.value === value}
              aria-disabled={opt.disabled}
              $focused={focusedIndex === idx}
              $selected={opt.value === value}
              $disabled={!!opt.disabled}
              onMouseEnter={() => !opt.disabled && setFocusedIndex(idx)}
              onClick={() => handleSelect(opt)}
            >
              {opt.label}
            </DropdownItem>
          ))}
        </DropdownList>,
        document.body
      )}
    </DropdownWrapper>
  );
};

export default Dropdown;
